<template>
    <div>

        <div class="alert alert-danger mt-10 mb-30" role="alert"
             v-show="hasErrors()">
            <b>Please correct the following errors:</b>
            <ul>
                <li v-for="error in errorsList">{{ error }}</li>
            </ul>
        </div>

        <div class="mt-30">

            <div class="data-table-container table-responsive">
                <table class="shifts data-table table">
                    <thead>
                    <tr>
                        <th>SL</th>
                        <th>Name</th>
                        <th>Short Name</th>
                        <th>Code</th>
                        <th>Day</th>
                        <th>Off-Day</th>
                        <th>Entry Time</th>
                        <th>Exit Time</th>
                        <th>Late Time</th>
                        <th>Early Leave Time</th>
                        <th></th>
                        <th>Default Shift</th>
                        <th>Active</th>
                        <th>Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    <template v-for="(shift, shiftIndex) in entries" :key="shiftIndex">
                        <tr v-for="(timing, timingIndex) in shift.timings" :key="timingIndex"
                            v-bind:class="{'odd': shiftIndex % 2 === 0}">
                            <td v-if="timingIndex === 0" rowspan="7">{{ shift.position }}</td>
                            <td v-if="timingIndex === 0" rowspan="7" class="w-150">
                                <div class="m-auto">
                                    <input type="text"
                                           autocomplete="off"
                                           title="Name"
                                           v-model="shift.name"
                                           class="form-control w-150"
                                           v-bind:class="{ 'is-invalid' : errors[`entries.${shiftIndex}.name`] }"/>
                                    <small class="invalid-feedback d-block"
                                           v-show="errors[`entries.${shiftIndex}.name`]">
                                        {{ errors[`entries.${shiftIndex}.name`] || '' }}
                                    </small>
                                </div>
                            </td>
                            <td v-if="timingIndex === 0" rowspan="7" class="w-80">
                                <div class="m-auto">
                                    <input type="text"
                                           autocomplete="off"
                                           title="Short Name"
                                           v-model="shift.short_name"
                                           class="form-control w-80"
                                           v-bind:class="{ 'is-invalid' : errors[`entries.${shiftIndex}.short_name`] }"/>
                                    <small class="invalid-feedback d-block"
                                           v-show="errors[`entries.${shiftIndex}.short_name`]">
                                        {{ errors[`entries.${shiftIndex}.short_name`] || '' }}
                                    </small>
                                </div>
                            </td>
                            <td v-if="timingIndex === 0" rowspan="7" class="w-80">
                                <div class="m-auto">
                                    <input type="text"
                                           autocomplete="off"
                                           title="Code "
                                           v-model="shift.code"
                                           class="form-control w-80"
                                           v-bind:class="{ 'is-invalid' : errors[`entries.${shiftIndex}.code`] }"/>
                                    <small class="invalid-feedback d-block"
                                           v-show="errors[`entries.${shiftIndex}.code`]">
                                        {{ errors[`entries.${shiftIndex}.code`] || '' }}
                                    </small>
                                </div>
                            </td>
                            <td v-bind:class="{'border-top-0': timingIndex > 0, 'pb-0': timingIndex < 6}" class="w-60">
                                {{ this.dayOfWeek(timing.week_day) }}
                            </td>
                            <td v-bind:class="{'border-top-0': timingIndex > 0, 'pb-0': timingIndex < 6}" class="w-40">
                                <div class="abc-checkbox abc-checkbox-circle abc-checkbox-success w-40">
                                    <input :id="`off-day-${shiftIndex}-${timingIndex}`" type="checkbox"
                                           v-model="timing.off_day"
                                           @change="offDayChanged(shiftIndex, timingIndex)"/>
                                    <label :for="`off-day-${shiftIndex}-${timingIndex}`" class="pl-20 pt-10"></label>
                                </div>
                            </td>
                            <td v-bind:class="{'border-top-0': timingIndex > 0, 'pb-0': timingIndex < 6}" class="w-150">
                                <TimePicker v-if="!timing.off_day"
                                            :ref="`start_time-${shiftIndex}-${timingIndex}`"
                                            :reference="`start_time-${shiftIndex}-${timingIndex}`"
                                            :on-change="onTimeChanged"
                                            :value="timing.start_time ? moment(timing.start_time).toDate(): null"
                                            :error="errors[`entries.${shiftIndex}.timings.${timingIndex}.start_time`]"/>
                            </td>
                            <td v-bind:class="{'border-top-0': timingIndex > 0, 'pb-0': timingIndex < 6}" class="w-150">
                                <TimePicker v-if="!timing.off_day"
                                            :ref="`end_time-${shiftIndex}-${timingIndex}`"
                                            :reference="`end_time-${shiftIndex}-${timingIndex}`"
                                            :on-change="onTimeChanged"
                                            :value="timing.end_time ? moment(timing.end_time).toDate() : null"
                                            :error="errors[`entries.${shiftIndex}.timings.${timingIndex}.end_time`]"/>
                            </td>
                            <td v-bind:class="{'border-top-0': timingIndex > 0, 'pb-0': timingIndex < 6}" class="w-150">
                                <TimePicker v-if="!timing.off_day"
                                            :ref="`late_time-${shiftIndex}-${timingIndex}`"
                                            :reference="`late_time-${shiftIndex}-${timingIndex}`"
                                            :on-change="onTimeChanged"
                                            :value="timing.late_time ? moment(timing.late_time).toDate() : null"
                                            :error="errors[`entries.${shiftIndex}.timings.${timingIndex}.late_time`]"/>
                            </td>
                            <td v-bind:class="{'border-top-0': timingIndex > 0, 'pb-0': timingIndex < 6}" class="w-150">
                                <TimePicker v-if="!timing.off_day"
                                            :ref="`early_leave_time-${shiftIndex}-${timingIndex}`"
                                            :reference="`early_leave_time-${shiftIndex}-${timingIndex}`"
                                            :on-change="onTimeChanged"
                                            :value="timing.early_leave_time ? moment(timing.early_leave_time).toDate() : null"
                                            :error="errors[`entries.${shiftIndex}.timings.${timingIndex}.early_leave_time`]"/>
                            </td>
                            <td v-bind:class="{'border-top-0': timingIndex > 0, 'pb-0': timingIndex < 6}" class="w-40">
                                <button v-if="timingIndex < 6"
                                        class="btn btn-icon btn-pure btn-default"
                                        data-original-title="Fill Down"
                                        data-toggle="tooltip"
                                        @click="fillDown(shiftIndex, timingIndex)">
                                    <i class="fa-solid fa-arrow-turn-down"></i>
                                </button>
                            </td>
                            <td v-if="timingIndex === 0" rowspan="7">
                                <div class="abc-checkbox abc-checkbox-circle abc-checkbox-success">
                                    <input :id="`default-shift-${shiftIndex}`" type="checkbox"
                                           v-model="shift.is_default"/>
                                    <label :for="`default-shift-${shiftIndex}`" class="pl-20 pt-10"></label>
                                </div>
                            </td>
                            <td v-if="timingIndex === 0" rowspan="7">
                                <div class="abc-checkbox abc-checkbox-circle abc-checkbox-success">
                                    <input :id="`active-${shiftIndex}`" type="checkbox"
                                           v-model="shift.active"/>
                                    <label :for="`active-${shiftIndex}`" class="pl-20 pt-10"></label>
                                </div>
                            </td>
                            <td v-if="timingIndex === 0" rowspan="7" class="w-80">
                                <div class="w-80 d-flex justify-content-center">
                                    <button class="btn btn-icon btn-pure btn-primary mr-5"
                                            data-original-title="Duplicate"
                                            data-toggle="tooltip"
                                            @click="duplicate(shiftIndex)">
                                        <i class="fa-solid fa-clone"></i>
                                    </button>
                                    <ActionButtons :entry="shift"
                                                   :index="shiftIndex"
                                                   :remove-entry="removeEntry"/>
                                </div>
                            </td>
                        </tr>
                    </template>
                    </tbody>
                </table>
            </div>

            <div v-if="hasAddPermission">
                <div class="mt-15 mb-15">
                    <button type="button" id="add-row-btn" class="btn btn-outline btn-primary w-100"
                            @click="addEntry(null)">
                        <i class="fa fa-plus"></i>
                        <span class="text ml-5">Add</span>
                    </button>
                </div>
            </div>

            <div>
                <button type="submit" class="ladda-button btn btn-primary w-100"
                        data-app-plugin="ladda" data-style="expand-left"
                        @click="submit">
                    Save
                </button>
            </div>

        </div>
    </div>
</template>

<script>
import {ref} from 'vue';
import TimePicker from "../../../components/TimePicker";
import ActionButtons from "../../../fees/collections/components/ActionButtons";
import moment from "moment";

export default {
    components: {
        TimePicker,
        ActionButtons,
    },
    props: {
        hasAddPermission: {
            type: Boolean,
            required: true,
        },
        hasDeletePermission: {
            type: Boolean,
            required: true,
        },
        shifts: {
            type: Array,
            required: true,
        },
        schoolId: {
            type: Number,
            required: true,
        }
    },
    setup(props) {
        return {
            entries: ref(props.shifts),
            errors: ref({}),
            errorsList: ref([]),
        }
    },
    methods: {
        moment(timeString, format) {
            moment.locale('en')
            return timeString ? moment(timeString, format ? format : 'HH:mm:ss') : moment()
        },
        initialErrorsState() {
            return {}
        },
        stopLoaders() {
            setTimeout(() => window.Ladda.stopAll(), 500)
        },
        hasErrors() {
            return this.errorsList.length > 0
        },
        dayOfWeek(dayNumber) {
            switch (dayNumber) {
                case 0:
                    return 'SUN'
                case 1:
                    return 'MON'
                case 2:
                    return 'TUE'
                case 3:
                    return 'WED'
                case 4:
                    return 'THU'
                case 5:
                    return 'FRI'
                case 6:
                    return 'SAT'
                default:
                    return null
            }
        },
        offDayChanged(shiftIndex, timingIndex) {
            if (this.entries[shiftIndex].timings[timingIndex].off_day) {
                this.entries[shiftIndex].timings[timingIndex].start_time = null
                this.entries[shiftIndex].timings[timingIndex].end_time = null
                this.entries[shiftIndex].timings[timingIndex].late_time = null
                this.entries[shiftIndex].timings[timingIndex].early_leave_time = null
            }
        },
        onTimeChanged(ref, time) {
            const parts = ref.split('-')
            this.entries[parts[1]].timings[Number(parts[2])][parts[0]] = time
        },
        fillDown(shiftIndex, timingIndex) {
            if (timingIndex >= 6) {
                return
            }

            this.entries[shiftIndex].timings[timingIndex + 1].off_day = this.entries[shiftIndex].timings[timingIndex].off_day
            this.entries[shiftIndex].timings[timingIndex + 1].start_time = this.entries[shiftIndex].timings[timingIndex].start_time
            this.entries[shiftIndex].timings[timingIndex + 1].end_time = this.entries[shiftIndex].timings[timingIndex].end_time
            this.entries[shiftIndex].timings[timingIndex + 1].late_time = this.entries[shiftIndex].timings[timingIndex].late_time
            this.entries[shiftIndex].timings[timingIndex + 1].early_leave_time = this.entries[shiftIndex].timings[timingIndex].early_leave_time

            const formattedTimeToDate = time => time ? this.moment(time, 'hh:mm A').toDate() : null

            this.$nextTick(() => {
                const newTimings = this.entries[shiftIndex].timings[timingIndex + 1]
                if (!newTimings.off_day) {
                    if (newTimings.start_time) {
                        this.$refs[`start_time-${shiftIndex}-${timingIndex + 1}`][0].forceUpdate(formattedTimeToDate(newTimings.start_time))
                    }
                    if (newTimings.end_time) {
                        this.$refs[`end_time-${shiftIndex}-${timingIndex + 1}`][0].forceUpdate(formattedTimeToDate(newTimings.end_time))
                    }
                    if (newTimings.late_time) {
                        this.$refs[`late_time-${shiftIndex}-${timingIndex + 1}`][0].forceUpdate(formattedTimeToDate(newTimings.late_time))
                    }
                    if (newTimings.early_leave_time) {
                        this.$refs[`early_leave_time-${shiftIndex}-${timingIndex + 1}`][0].forceUpdate(formattedTimeToDate(newTimings.early_leave_time))
                    }
                }
            })
        },
        duplicate(shiftIndex) {
            this.addEntry(this.entries[shiftIndex])
        },
        addEntry(sourceEntry) {
            const formatTime = time => time ? moment(time, 'hh:mm A').format('HH:mm:ss') : null

            let timings = []
            for (let i = 0; i <= 6; i++) {
                timings.push(
                    {
                        id: null,
                        shift_id: null,
                        week_day: i,
                        off_day: sourceEntry?.timings[i].off_day ?? false,
                        start_time: formatTime(sourceEntry?.timings[i]?.start_time),
                        end_time: formatTime(sourceEntry?.timings[i]?.end_time),
                        late_time: formatTime(sourceEntry?.timings[i]?.late_time),
                        early_leave_time: formatTime(sourceEntry?.timings[i]?.early_leave_time),
                    }
                )
            }

            this.entries.push({
                id: null,
                school_id: this.schoolId,
                name: sourceEntry?.name,
                short_name: sourceEntry?.short_name,
                code: sourceEntry?.code,
                is_default: false,
                position: this.entries.length,
                active: true,
                timings,
            })
        },
        removeEntry(index) {
            this.entries.splice(index, 1)
        },
        async submit() {
            this.clearErrors()

            const response = await fetch(
                route('employee-shifts.store'),
                {
                    method: 'put',
                    headers: new Headers({
                        'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
                        'X-Requested-With': 'XMLHttpRequest',
                        'Content-Type': 'application/json'
                    }),
                    credentials: 'include',  // adds cookie header
                    body: JSON.stringify({entries: this.entries})
                }
            )
            if (response.status === 422) {
                const json = await response.json()
                this.showSubmissionErrors(this.transformSubmissionErrors(json.errors))
            } else if (!response.ok) {
                const error = await response.text()
                toastr.error(`Failed updating employee shifts: ${error}`)
            } else {
                toastr.success('Employee Shifts updated successfully')
                window.location = route('employee-shifts.index')
            }

            this.stopLoaders()
        },
        transformSubmissionErrors(serverSideErrors) {
            const errors = this.initialErrorsState()
            let errorsList = []

            for (let prop in serverSideErrors) {
                const propParts = prop.split('.')
                if (propParts.length === 2) {
                    errors[propParts[0]][propParts[1]] = serverSideErrors[prop][0]
                } else {
                    errors[prop] = serverSideErrors[prop][0]
                }

                errorsList.push(serverSideErrors[prop][0])
            }

            this.errorsList = errorsList

            return errors
        },
        showSubmissionErrors(errors) {
            console.log(errors)
            this.errors = errors
            toastr.error(`Please correct the errors specified.`)
            window.scrollTo({top: 0, behavior: 'smooth'})
        },
        clearErrors() {
            this.errors = this.initialErrorsState()
            this.errorsList = []
        }
    },
}
</script>

<style scoped>
    table.shifts tr.odd td {
        background-color: #f2f8ff;
    }
    table.shifts td {
        vertical-align: middle;
        text-align: center;
    }
    table.shifts td:nth-child(1),
    table.shifts td:nth-child(2),
    table.shifts td:nth-child(3),
    table.shifts td:nth-child(4),
    table.shifts td:nth-child(11),
    table.shifts td:nth-child(12),
    table.shifts td:nth-child(13),
    table.shifts td:nth-child(14) {
        vertical-align: top;
    }
    table.shifts .abc-checkbox {
        margin-top: 7px;
    }
</style>
